import type { AdminRoadmapDetail } from '@prepzio/shared';

/** Mermaid label text is finicky — strip quotes/brackets, collapse space, truncate. */
export function label(s: string, max = 60): string {
  return (
    s
      .replace(/["#<>{}()[\]|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, max) || '—'
  );
}

/** Build a `graph TD` definition: roadmap → day-items (stations) → sessions → ordered questions. */
export function buildMermaid(d: AdminRoadmapDetail): string {
  const lines: string[] = ['graph TD'];
  lines.push(`  R["🗺️ ${label(d.roadmap.title)}<br/><small>${d.roadmap.status}</small>"]`);

  // Sessions grouped by the roadmap-item they belong to (+ ones attached to the roadmap itself).
  const byItem = new Map<string, AdminRoadmapDetail['sessions']>();
  const rootSessions: AdminRoadmapDetail['sessions'] = [];
  for (const s of d.sessions) {
    if (s.roadmapItemId) {
      const arr = byItem.get(s.roadmapItemId) ?? [];
      arr.push(s);
      byItem.set(s.roadmapItemId, arr);
    } else {
      rootSessions.push(s);
    }
  }

  const kindMark: Record<string, string> = {
    main_goal: '🎯',
    practice: '📝',
    reflection: '💭',
  };

  const renderSession = (s: AdminRoadmapDetail['sessions'][number], parent: string, si: number) => {
    const sid = `${parent}_s${si}`;
    lines.push(`  ${sid}(["▶ ${label(s.sessionType)} · ${label(s.status, 12)}"])`);
    lines.push(`  ${parent} --> ${sid}`);
    s.questions.forEach((q, qi) => {
      const qid = `${sid}_q${qi}`;
      lines.push(
        `  ${qid}["${q.orderIndex + 1}. ${label(q.prompt, 50)}<br/><small>${label(q.type, 12)} · ${label(q.difficulty, 8)}</small>"]`,
      );
      lines.push(`  ${sid} --> ${qid}`);
    });
    if (s.questions.length === 0) {
      lines.push(`  ${sid}_none["(no questions)"]`);
      lines.push(`  ${sid} --> ${sid}_none`);
    }
  };

  d.items.forEach((it, i) => {
    const nid = `i${i}`;
    const mark = kindMark[it.kind] ?? '•';
    const skill = it.skillTitle ? ` · ${label(it.skillTitle, 20)}` : '';
    lines.push(
      `  ${nid}["${mark} D${it.dayIndex} ${label(it.kind, 12)}: ${label(it.title, 40)}${skill}"]`,
    );
    lines.push(`  R --> ${nid}`);
    (byItem.get(it.id) ?? []).forEach((s, si) => renderSession(s, nid, si));
  });

  rootSessions.forEach((s, si) => renderSession(s, 'R', si + 900));

  if (d.items.length === 0 && d.sessions.length === 0) {
    lines.push('  empty["(this roadmap has no items or sessions yet)"]');
    lines.push('  R --> empty');
  }
  return lines.join('\n');
}
